
import React from 'react';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { X, Plus } from 'lucide-react';
import { EVALUATION_ROUNDS, getEvaluationRoundLabel } from '@/types/enums';

export interface ScoreFilterCriteria {
  id: string;
  round: string;
  minScore: number;
  maxScore?: number;
}

interface ScoreFilterProps {
  scoreFilters: ScoreFilterCriteria[];
  onAddFilter: (filter: Omit<ScoreFilterCriteria, 'id'>) => void;
  onRemoveFilter: (id: string) => void;
  onClearAll: () => void;
}

export function ScoreFilter({ scoreFilters, onAddFilter, onRemoveFilter, onClearAll }: ScoreFilterProps) {
  const [selectedRound, setSelectedRound] = React.useState<string>('');
  const [minScore, setMinScore] = React.useState<string>('');
  const [maxScore, setMaxScore] = React.useState<string>('100');

  const handleAddFilter = () => {
    if (!selectedRound || !minScore) return;

    const filter: Omit<ScoreFilterCriteria, 'id'> = {
      round: selectedRound,
      minScore: parseFloat(minScore),
      maxScore: maxScore ? parseFloat(maxScore) : 100,
    };

    onAddFilter(filter);
    setSelectedRound('');
    setMinScore('');
    setMaxScore('100');
  };

  const getRoundDisplayName = (round: string) => {
    if (round === 'cgpa') return 'CGPA';
    if (round === 'final_score') return 'Final Score';
    if (round === 'assessment_round') return 'Assessment Round';
    return getEvaluationRoundLabel(round as any);
  };

  const availableRounds = [
    ...Object.values(EVALUATION_ROUNDS),
    'cgpa',
    'final_score',
    'assessment_round'
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-2">
        <div className="flex-1 min-w-[150px]">
          <label className="text-sm font-medium mb-1 block">Round/Criteria</label>
          <Select value={selectedRound} onValueChange={setSelectedRound}>
            <SelectTrigger>
              <SelectValue placeholder="Select round" />
            </SelectTrigger>
            <SelectContent>
              {availableRounds.map((round) => (
                <SelectItem key={round} value={round}>
                  {getRoundDisplayName(round)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div className="flex-1 min-w-[80px]">
          <label className="text-sm font-medium mb-1 block">Min Score</label>
          <Input
            type="number"
            placeholder="Min"
            value={minScore}
            onChange={(e) => setMinScore(e.target.value)}
            min="0"
            max="100"
          />
        </div>
        
        <div className="flex-1 min-w-[80px]">
          <label className="text-sm font-medium mb-1 block">Max Score</label>
          <Input
            type="number"
            placeholder="Max(100)"
            value={maxScore}
            onChange={(e) => setMaxScore(e.target.value)}
            min="0"
            max="100"
          />
        </div>
        
        <Button onClick={handleAddFilter} disabled={!selectedRound || !minScore}>
          <Plus className="h-4 w-4 mr-1" />
          Add
        </Button>
      </div>

      {scoreFilters.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Active Score Filters:</span>
            <Button variant="outline" size="sm" onClick={onClearAll}>
              Clear All
            </Button>
          </div>
          <div className="flex flex-wrap gap-2">
            {scoreFilters.map((filter) => (
              <Badge key={filter.id} variant="secondary" className="flex items-center gap-1">
                {getRoundDisplayName(filter.round)}: {filter.minScore}
                {filter.maxScore ? `-${filter.maxScore}` : '+'}
                <button
                  onClick={() => onRemoveFilter(filter.id)}
                  className="ml-1 hover:bg-gray-200 rounded-full p-0.5"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
